export class QuickAdd extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.modalContent = null;
    this.setupModal();
    this.bindEvents();
    this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
    this.resetAllSpinners = this.resetAllSpinners.bind(this);
    this.setupAjaxCartButtons();
  }

  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;
      document.body.appendChild(this);
      document.addEventListener('cart:change', this.onCartRequestEnd);
      document.addEventListener('cart:error', this.resetAllSpinners);
    }
  }

  setupAjaxCartButtons() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!(form.getAttribute('action') || '').includes('/cart/add')) return;

      const button = form.querySelector('.quick-add__icon-button');
      if (button) this.toggleSpinner(button, true);
    });
  }

  toggleSpinner(button, show) {
    const spinner = button.querySelector('.add-to-cart-icon-spinner');
    const icon = button.querySelector('.add-to-cart-icon');
    const text = button.querySelector('.add-to-cart-text__content');

    spinner?.classList.toggle('hidden', !show);
    icon?.classList.toggle('hidden', show);
    text?.classList.toggle('hidden', show);
  }

  disconnectedCallback() {
    if (this.initialized) {
      document.removeEventListener('cart:change', this.onCartRequestEnd);
      document.removeEventListener('cart:error', this.resetAllSpinners);
    }
  }

  onCartRequestEnd(event) {
    if (event.detail?.action === 'add') {
      document.body.classList.remove('overflow-hidden');

      document.querySelectorAll('quick-add-modal').forEach((modal) => {
        modal.removeAttribute('open');
        if (modal.modalContent) {
          modal.modalContent.innerHTML = '';
        }
      });

      this.resetAllSpinners();
    }
  }

  resetAllSpinners() {
    document.querySelectorAll('.quick-add__icon-button').forEach((button) => {
      this.toggleSpinner(button, false);
    });
  }

  setupModal() {
    this.modal = this.querySelector('[role="dialog"]');
    this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
  }

  bindEvents() {
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => this.hide());
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  show(opener) {
    this.openedBy = opener;

    if (opener && opener.getAttribute('data-product-url')) {
      opener.setAttribute('aria-disabled', true);

      const productUrl = opener.getAttribute('data-product-url');

      fetch(productUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const doc = new DOMParser().parseFromString(responseText, 'text/html');

          /* By hook, not by tag. This asked for `product-info`, which only
             sections/product.liquid renders - the `product` template uses
             main-product-template4 with a <product-detail-hero> root, so the
             lookup returned null for every product and no dialog ever opened.
             The tag name stays as a fallback for anything not yet marked. */
          const productElement = doc.querySelector('[data-product-info], product-info');

          if (!productElement) {
            console.error(
              'quick-add: no [data-product-info] element in the response for ' +
                productUrl +
                '. The product template root element needs a data-product-info attribute.',
            );
            return;
          }

          this.adoptAssets(doc);
          this.preprocessContent(productElement);
          this.setContent(productElement.outerHTML);

          document.body.classList.add('overflow-hidden');
          this.setAttribute('open', '');

          this.resetAllSpinners();

          if (window.Shopify?.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
        })
        .catch((error) => {
          console.error('Error loading product:', error);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          this.resetAllSpinners();
        });
    } else {
      // For other modals (like monogram popup) that don't need fetch
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    if (this.modalContent) {
      this.modalContent.innerHTML = '';
    }
  }

  /* A section loads its own CSS and JS with stylesheet_tag and a module
     script, and those tags sit outside the section element - so none of them
     travel with productElement.outerHTML. Injected without them the dialog
     showed unstyled markup whose custom elements were never defined, leaving
     the variant picker as inert radio buttons: the product template’s CSS
     and JS are not otherwise loaded on the homepage.

     Only theme assets are copied. Everything a Shopify page loads from
     /assets/ is ours; the analytics, app and checkout scripts on the same
     page are not, and re-running those here would be both pointless and
     risky. Already-present URLs are skipped, so opening several dialogs adds
     nothing twice. */
  adoptAssets(doc) {
    const isThemeAsset = (url) => url.pathname.includes('/assets/');

    const pathsOf = (selector, attribute) =>
      new Set(
        Array.from(document.querySelectorAll(selector), (element) => {
          try {
            return new URL(element.getAttribute(attribute), location.href).pathname;
          } catch {
            return null;
          }
        }).filter(Boolean),
      );

    const haveStyles = pathsOf('link[rel="stylesheet"][href]', 'href');
    for (const link of doc.querySelectorAll('link[rel="stylesheet"][href]')) {
      let url;
      try {
        url = new URL(link.getAttribute('href'), location.href);
      } catch {
        continue;
      }
      if (!isThemeAsset(url) || haveStyles.has(url.pathname)) continue;
      haveStyles.add(url.pathname);
      const copy = document.createElement('link');
      copy.rel = 'stylesheet';
      copy.href = url.href;
      document.head.appendChild(copy);
    }

    const haveScripts = pathsOf('script[src]', 'src');
    for (const script of doc.querySelectorAll('script[src]')) {
      let url;
      try {
        url = new URL(script.getAttribute('src'), location.href);
      } catch {
        continue;
      }
      if (!isThemeAsset(url) || haveScripts.has(url.pathname)) continue;
      haveScripts.add(url.pathname);
      const copy = document.createElement('script');
      copy.src = url.href;
      if (script.type) copy.type = script.type;
      /* The markup is already in the document, so whenever these definitions
         arrive the browser upgrades it. No ordering to manage. */
      document.head.appendChild(copy);
    }
  }

  preprocessContent(element) {
    const uniqueSectionId = `${element.dataset.section}-modal-${Date.now()}`;
    element.innerHTML = element.innerHTML.replaceAll(element.dataset.section, uniqueSectionId);
    element.dataset.section = uniqueSectionId;
    element.dataset.updateUrl = 'false';
    element.querySelector('pickup-availability')?.remove();
    element.querySelector('script[src*="component-pickup-availability.js"]')?.remove();
    element.querySelector('product-media-modal')?.remove();
    element.querySelector('script[src*="component-product-media-modal.js"]')?.remove();
    element.querySelector('product-recommendations')?.remove();
    element.querySelector('script[src*="product-recommendations.js"]')?.remove();
  }

  setContent(html) {
    this.modalContent.innerHTML = html;
    // Reinject scripts
    this.modalContent.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }
}

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', QuickAdd);
}
